// src/ProprioApp.jsx — ESPACE PROPRIÉTAIRE, refonte 4 onglets (spec Dimitri
// 2026-08-22, lot ① squelette).
//
// LE MENU PASSE DE 7 À 4 (décision Dimitri) :
//   🏠 Accueil · 💶 Mes revenus (Statistiques / Factures) · 📅 Mon calendrier ·
//   ✨ Mon bien (Avis / Ménage & incidents — scindé en 3 au lot ⑤).
//   « Ma fiche » quitte la barre → pastille d'identité en haut à droite.
//   « Messages » quitte la barre → rattaché aux séjours du calendrier (lot ④) ;
//   EN ATTENDANT ce lot, la vue Messages reste atteignable par la bascule du
//   calendrier (zéro régression : chaque fonction reste accessible).
//
// LA GRAMMAIRE COMMUNE (mêmes écrans ordinateur/téléphone que les espaces
// orchestrateur et agent) : barre du lotus → onglets (ordinateur) → bandeau
// navy → bascule de page (BasculeEspace, 2 habits) → contenu → barre du bas
// (BarreOngletsBas, téléphone). Le seuil des deux habits = useEtroit (860 px).
//
// AUCUNE MÉMOIRE de sous-page : changer d'onglet ré-ouvre toujours la première
// entrée de la bascule (règle « comme partout ailleurs », correction §8).
//
// Le cloisonnement NE BOUGE PAS : toutes les vues restent en LECTURE SEULE et
// consomment src/lib/proprio-api.js (endpoints proprio_* cloisonnés logIds
// serveur). La nav est un confort UX — le VRAI cloisonnement reste le backend.
//
// Hoguet : « conciergerie », jamais « gestion / mandat ».

import { lazy, Suspense, useEffect, useState } from 'react'
import {
  Home, Banknote, CalendarDays, Sparkles, User, LogOut, ChevronDown,
  ChevronLeft, ChevronRight,
} from 'lucide-react'
import { DS } from './lib/ui'
import { LOGO_B64 } from './assets/logo'
import useEtroit from './lib/useEtroit'
import BasculeEspace from './components/BasculeEspace'
import BarreOngletsBas from './components/BarreOngletsBas'
import ProprioAccueilView from './views/proprio/ProprioAccueilView'
import ProprioLaneGanttView from './views/proprio/ProprioLaneGanttView'
import ProprioMessagesView from './views/proprio/ProprioMessagesView'
import ProprioHistoriqueView from './views/proprio/ProprioHistoriqueView'
import ProprioAvisView from './views/proprio/ProprioAvisView'
import ProprioMenageView from './views/proprio/ProprioMenageView'
import ProprioFicheView from './views/proprio/ProprioFicheView'
import { Loading } from './views/proprio/_proprioUi'

// Lazy : la vue stats (donuts/tableau) n'est chargée qu'à l'ouverture de
// « Mes revenus » (même politique que l'ancien ProprioFacturationHub).
const ProprioStatsView = lazy(() => import('./views/proprio/ProprioStatsView'))

// Les 4 onglets (ordre = nav). `court` = libellé de la barre du bas téléphone.
const PROPRIO_TABS = [
  { id: 'accueil', label: 'Accueil', court: 'Accueil', icone: Home },
  { id: 'revenus', label: 'Mes revenus', court: 'Revenus', icone: Banknote },
  { id: 'calendrier', label: 'Mon calendrier', court: 'Calendrier', icone: CalendarDays },
  { id: 'bien', label: 'Mon bien', court: 'Bien', icone: Sparkles },
]

// Bascules de page par onglet (première entrée = défaut, jamais mémorisé).
const BASCULES = {
  revenus: [
    { id: 'stats', label: 'Statistiques' },
    { id: 'factures', label: 'Factures' },
  ],
  // Calendrier (correction Dimitri 22/08) : la grille « Mois » disparaît (elle
  // disait moins que le Planning). DEUX CHEMINS vers les messages, VOULU :
  // la bascule (vue d'ensemble, tous logements) ET la feuille d'un séjour
  // (les échanges de CE voyageur).
  calendrier: [
    { id: 'planning', label: 'Planning' },
    { id: 'messages', label: 'Messages' },
  ],
  // Scission lot ⑤ (spec §6) : trois pages sous Mon bien.
  bien: [
    { id: 'avis', label: 'Avis' },
    { id: 'menages', label: 'Ménages' },
    { id: 'incidents', label: 'Incidents' },
  ],
}

// L'ancienne nav 7 onglets → la nouvelle (onglet + sous-page). Les cartes de
// l'Accueil (onNavigate) parlent encore l'ancien vocabulaire — on traduit ICI
// plutôt que de retoucher la vue (elle sera refaite au lot ②).
export function mapAncienneCible(cible) {
  switch (cible) {
    case 'facturation': return { tab: 'revenus', sous: 'factures' }
    case 'avis': return { tab: 'bien', sous: 'avis' }
    case 'menage': return { tab: 'bien', sous: 'menages' }
    case 'messages': return { tab: 'calendrier', sous: 'messages' }
    case 'calendrier': return { tab: 'calendrier', sous: 'planning' }
    case 'fiche': return { tab: 'fiche', sous: null }
    default: return { tab: 'accueil', sous: null }
  }
}

// Prénom RÉEL du compte (first_name / prenom), trimé — JAMAIS dérivé de l'email
// (règle Dimitri #4 : « prénom réel ou RIEN », pas de faux prénom fabriqué).
export function realFirstName(currentUser) {
  return (currentUser?.first_name || currentUser?.prenom || '').trim()
}

// Initiale d'avatar = 1ʳᵉ lettre du prénom réel, ou null si aucun prénom (→ pas
// de pastille « P » générique tirée de l'email/rôle). Présentation pure.
export function avatarInitial(currentUser) {
  const n = realFirstName(currentUser)
  return n ? n.charAt(0).toUpperCase() : null
}

const DEGRADE_BANDEAU =
  'linear-gradient(115deg, #233240 0%, #3d5166 45%, #5c8ab8 100%)'

// Titre du bandeau navy par page (générique — chaque lot d'écran enrichit SON
// bandeau : héro revenus, flèches de mois…).
const TITRES = {
  accueil: 'Accueil', revenus: 'Mes revenus', calendrier: 'Mon calendrier',
  bien: 'Mon bien', fiche: 'Ma fiche',
}

// Les pages qui apportent LEUR bandeau navy (le shell s'efface alors) :
// l'Accueil rend son héro revenus (lot ②).
const BANDEAU_PROPRE = new Set(['accueil'])

// Mois courant {y, m1} (présentation, heure locale — le calendrier est local).
function moisCourant() {
  const d = new Date()
  return { y: d.getFullYear(), m1: d.getMonth() + 1 }
}
const NOMS_MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet',
  'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

export default function ProprioApp({ currentUser, onLogout }) {
  const etroit = useEtroit()
  const [tab, setTab] = useState('accueil')
  const [sous, setSous] = useState(null) // sous-page de la bascule courante
  const [menuOuvert, setMenuOuvert] = useState(false)
  // LES FLÈCHES DE MOIS vivent DANS le bandeau (spec §2) — le mois du
  // calendrier est piloté ICI et passé aux deux sous-vues. Jamais mémorisé :
  // revenir sur l'onglet ré-ouvre le mois courant. Le futur reste navigable
  // (un calendrier de résas regarde devant — la « flèche morte » vaut pour les
  // pages tournées vers le passé, comme Mon travail).
  const [moisCal, setMoisCal] = useState(moisCourant)

  // Changer d'onglet ré-ouvre TOUJOURS la première entrée (aucune mémoire).
  const allerA = (id, sousVoulu = null) => {
    setTab(id)
    setSous(sousVoulu || (BASCULES[id] ? BASCULES[id][0].id : null))
    setMenuOuvert(false)
    if (id === 'calendrier') setMoisCal(moisCourant())
  }
  const bougerMois = (dir) => {
    setMoisCal(({ y, m1 }) => {
      const d = new Date(y, m1 - 1 + dir, 1)
      return { y: d.getFullYear(), m1: d.getMonth() + 1 }
    })
  }
  const naviguerAncien = (cible) => {
    const { tab: t, sous: s } = mapAncienneCible(cible)
    allerA(t, s)
  }

  // Échap ferme le menu d'identité.
  useEffect(() => {
    if (!menuOuvert) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setMenuOuvert(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOuvert])

  const initiale = avatarInitial(currentUser)
  const prenom = realFirstName(currentUser)
  const basculeEntrees = BASCULES[tab] || null
  const sousActif = sous || (basculeEntrees ? basculeEntrees[0].id : null)

  return (
    <div className="fbz-app" style={{ fontFamily: DS.fontBody, minHeight: '100vh', background: '#ffffff' }}>
      {/* ── 1. LA BARRE DU LOTUS — fond blanc, filet #e3e8ee ── */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e3e8ee' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: etroit ? '9px 12px' : '9px 16px',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Correction à l'œil (22/08) : lotus 26→34, marque 12→15 — l'en-tête
              se remarquait à peine. */}
          <img src={`data:image/png;base64,${LOGO_B64}`} alt="logo"
            style={{ width: 34, height: 34, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
          {!etroit && (
            <span style={{ fontFamily: DS.fontDisplay, fontWeight: 700, fontSize: 15,
              color: '#3d5166', letterSpacing: '.06em' }}>FULLBNBZEN</span>
          )}
          <div style={{ flex: 1 }} />
          {/* Pastille d'identité → Ma fiche + déconnexion. */}
          <div style={{ position: 'relative' }}>
            <button type="button" data-testid="proprio-identite"
              onClick={() => setMenuOuvert((v) => !v)}
              aria-expanded={menuOuvert}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                fontFamily: DS.fontBody }}>
              <span aria-hidden="true"
                style={{ width: etroit ? 32 : 24, height: etroit ? 32 : 24, borderRadius: '50%',
                  background: '#3d5166', color: '#fff', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: etroit ? 13 : 11, fontWeight: 700 }}>
                {initiale || <User size={etroit ? 15 : 12} strokeWidth={2.2} color="#fff" />}
              </span>
              {!etroit && prenom && (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#233240' }}>{prenom}</span>
              )}
              {!etroit && <ChevronDown size={14} strokeWidth={2} color="#5c6773" />}
            </button>
            {menuOuvert && (
              <>
                {/* Voile invisible : un clic hors du menu le ferme. */}
                <div onClick={() => setMenuOuvert(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
                <div data-testid="proprio-menu-identite" role="menu"
                  style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 91,
                    background: '#fff', border: '1px solid #e5eaf0', borderRadius: 10,
                    boxShadow: '0 6px 22px rgba(35,50,64,.14)', minWidth: 168, padding: 5 }}>
                  <button type="button" role="menuitem" data-testid="menu-ma-fiche"
                    onClick={() => allerA('fiche')}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '9px 11px', border: 'none', background: 'none', borderRadius: 7,
                      fontSize: 12.5, fontWeight: 600, color: '#233240', cursor: 'pointer',
                      fontFamily: DS.fontBody }}>
                    <User size={15} strokeWidth={2} color="#3d5166" /> Ma fiche
                  </button>
                  <button type="button" role="menuitem" data-testid="menu-deconnexion"
                    onClick={onLogout}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '9px 11px', border: 'none', background: 'none', borderRadius: 7,
                      fontSize: 12.5, fontWeight: 600, color: '#8f2f1d', cursor: 'pointer',
                      fontFamily: DS.fontBody }}>
                    <LogOut size={15} strokeWidth={2} color="#8f2f1d" /> Déconnexion
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── 2. LES 4 ONGLETS (ordinateur) — boîte #f6f8fa, actif blanc ombré ── */}
      {!etroit && (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '12px 16px 0' }}>
          <nav data-testid="proprio-onglets"
            style={{ display: 'inline-flex', gap: 3, background: '#f6f8fa',
              borderRadius: 10, padding: 3 }}>
            {PROPRIO_TABS.map((t) => {
              const on = tab === t.id
              const Icone = t.icone
              return (
                <button key={t.id} type="button" data-testid={`onglet-${t.id}`}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => allerA(t.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    minHeight: 42, padding: '0 16px', borderRadius: 8, border: 'none',
                    background: on ? '#fff' : 'none',
                    boxShadow: on ? '0 1px 4px rgba(35,50,64,.14)' : 'none',
                    color: on ? '#233240' : '#5c6773', fontSize: 12.5,
                    fontWeight: on ? 700 : 500, cursor: on ? 'default' : 'pointer',
                    whiteSpace: 'nowrap', fontFamily: DS.fontBody }}>
                  <Icone size={15} strokeWidth={1.9} color={on ? '#3d5166' : '#98a2ad'} />
                  {t.label}
                </button>
              )
            })}
          </nav>
        </div>
      )}

      {/* ── 3. LE BANDEAU NAVY — dégradé 115°, rayon 12 (pleine largeur au
             téléphone). Générique ; une page de BANDEAU_PROPRE rend le sien.
             SANS filigrane (correction Dimitri 22/08 — « ce n'est pas beau »,
             les quatre bandeaux se ressemblent). ── */}
      {!BANDEAU_PROPRE.has(tab) && (
      <div style={etroit ? {} : { maxWidth: 960, margin: '0 auto', padding: '12px 16px 0' }}>
        <div data-testid="proprio-bandeau"
          style={{ position: 'relative', overflow: 'hidden', background: DEGRADE_BANDEAU,
            borderRadius: etroit ? 0 : 12, padding: etroit ? '14px 12px' : '14px 18px',
            color: '#fff' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: DS.fontDisplay, fontSize: 19, fontWeight: 700 }}>
                {TITRES[tab] || 'Accueil'}
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
                Le suivi de vos logements, en un coup d'œil.
              </div>
            </div>
            {/* LES FLÈCHES DE MOIS (spec §2) — deux boutons 32×32, le mois au
                centre en blanc gras. Elles pilotent les deux sous-vues. */}
            {tab === 'calendrier' && sousActif === 'planning' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" data-testid="bandeau-mois-prec"
                  aria-label="Mois précédent" onClick={() => bougerMois(-1)}
                  className="fbz-press"
                  style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
                    background: 'rgba(255,255,255,.14)', color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft size={16} strokeWidth={2.2} />
                </button>
                <span data-testid="bandeau-mois" style={{ fontSize: 12.5, fontWeight: 700,
                  minWidth: 92, textAlign: 'center' }}>
                  {NOMS_MOIS[moisCal.m1 - 1]} {moisCal.y}
                </span>
                <button type="button" data-testid="bandeau-mois-suiv"
                  aria-label="Mois suivant" onClick={() => bougerMois(1)}
                  className="fbz-press"
                  style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
                    background: 'rgba(255,255,255,.14)', color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={16} strokeWidth={2.2} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ── 4. LA BASCULE DE PAGE (2 habits BasculeEspace) ── */}
      {basculeEntrees && (
        <div style={etroit ? {} : { maxWidth: 960, margin: '0 auto', padding: '12px 16px 0' }}>
          {!etroit && <div style={{ height: 0 }} />}
          <BasculeEspace testId="bascule-proprio" entrees={basculeEntrees}
            actif={sousActif} onChange={(id) => setSous(id)} />
        </div>
      )}

      {/* ── 5. LE CONTENU ── */}
      <main style={{ maxWidth: 960, margin: '0 auto', padding: etroit ? '4px 12px 48px' : '6px 16px 48px' }}>
        {tab === 'accueil' && <ProprioAccueilView currentUser={currentUser} onNavigate={naviguerAncien} />}
        {tab === 'revenus' && sousActif === 'stats' && (
          <Suspense fallback={<Loading label="Chargement des statistiques…" />}>
            <ProprioStatsView />
          </Suspense>
        )}
        {tab === 'revenus' && sousActif === 'factures' && <ProprioHistoriqueView />}
        {tab === 'calendrier' && sousActif === 'planning' && <ProprioLaneGanttView mois={moisCal} />}
        {tab === 'calendrier' && sousActif === 'messages' && <ProprioMessagesView />}
        {tab === 'bien' && sousActif === 'avis' && <ProprioAvisView />}
        {tab === 'bien' && sousActif === 'menages' && <ProprioMenageView partie="menages" />}
        {tab === 'bien' && sousActif === 'incidents' && <ProprioMenageView partie="incidents" />}
        {tab === 'fiche' && <ProprioFicheView currentUser={currentUser} />}
      </main>

      {/* ── LA BARRE DU BAS (téléphone seulement — le composant se garde) ── */}
      <BarreOngletsBas
        categories={PROPRIO_TABS.map((t) => ({ id: t.id, label: t.court }))}
        activeId={tab}
        onChoisir={(id) => allerA(id)} />
    </div>
  )
}
